import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface AccessibilityPreferences {
  // Visual
  prefersHighContrast: boolean;
  prefersLargeText: boolean;
  prefersReducedMotion: boolean;
  colorVisionMode: "protanopia" | "deuteranopia" | "tritanopia" | null;
  
  // Audio
  prefersAudioDescriptions: boolean;
  prefersCaptions: boolean;
  
  // Interaction
  prefersKeyboardNavigation: boolean;
  prefersSimplifiedUI: boolean;
  needsExtraTime: boolean;
  
  // Learning
  learningStyle: string[];
  prefersStepByStep: boolean;
  prefersVideoTutorials: boolean;
  
  // Communication
  preferredLanguage: string;
  readingLevel: "simplified" | "standard" | "advanced";
}

export interface CulturalPreferences {
  preferredPronouns: "she_her" | "he_him" | "they_them" | "ze_zir" | "custom" | "prefer_not" | "ask_me" | null;
  customPronouns: string | null;
  preferredHonorific: "none" | "mr" | "ms" | "mx" | "dr" | "custom" | null;
  customHonorific: string | null;
  culturalBackground: string[];
  primaryLanguageAtHome: string | null;
  prefersCulturallyDiverseExamples: boolean;
  regionalContentPreference: string | null;
  contentSensitivityFlags: string[];
}

interface AccessibilityContextType {
  preferences: AccessibilityPreferences;
  culturalPreferences: CulturalPreferences;
  updatePreferences: (updates: Partial<AccessibilityPreferences>) => void;
  updateCulturalPreferences: (updates: Partial<CulturalPreferences>) => void;
  savePreferences: () => Promise<void>;
  isLoading: boolean;
  applyAccessibilityClasses: () => string;
}

const defaultAccessibilityPreferences: AccessibilityPreferences = {
  prefersHighContrast: false,
  prefersLargeText: false,
  prefersReducedMotion: false,
  colorVisionMode: null,
  prefersAudioDescriptions: false,
  prefersCaptions: true,
  prefersKeyboardNavigation: false,
  prefersSimplifiedUI: false,
  needsExtraTime: false,
  learningStyle: [],
  prefersStepByStep: false,
  prefersVideoTutorials: false,
  preferredLanguage: "en",
  readingLevel: "standard",
};

const defaultCulturalPreferences: CulturalPreferences = {
  preferredPronouns: null,
  customPronouns: null,
  preferredHonorific: null,
  customHonorific: null,
  culturalBackground: [],
  primaryLanguageAtHome: null,
  prefersCulturallyDiverseExamples: true,
  regionalContentPreference: null,
  contentSensitivityFlags: [],
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error("useAccessibility must be used within an AccessibilityProvider");
  }
  return context;
};

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(() => {
    // Load from localStorage first for immediate application
    const stored = localStorage.getItem("accessibility_preferences");
    if (stored) {
      try {
        return { ...defaultAccessibilityPreferences, ...JSON.parse(stored) };
      } catch {
        return defaultAccessibilityPreferences;
      }
    }
    // Check system preferences
    const systemPrefers = {
      ...defaultAccessibilityPreferences,
      prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      prefersHighContrast: window.matchMedia("(prefers-contrast: more)").matches,
    };
    return systemPrefers;
  });
  
  const [culturalPreferences, setCulturalPreferences] = useState<CulturalPreferences>(defaultCulturalPreferences);
  const [isLoading, setIsLoading] = useState(false);

  // Load preferences from database when user is logged in
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        // Load accessibility preferences
        const { data: accessData } = await supabase
          .from("user_accessibility_preferences")
          .select("prefers_high_contrast, prefers_large_text, prefers_reduced_motion, color_vision_mode, prefers_audio_descriptions, prefers_captions, prefers_keyboard_navigation, prefers_simplified_ui, needs_extra_time, learning_style, prefers_step_by_step, prefers_video_tutorials, preferred_language, reading_level")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (accessData) {
          setPreferences({
            prefersHighContrast: accessData.prefers_high_contrast ?? false,
            prefersLargeText: accessData.prefers_large_text ?? false,
            prefersReducedMotion: accessData.prefers_reduced_motion ?? false,
            colorVisionMode: accessData.color_vision_mode as AccessibilityPreferences["colorVisionMode"],
            prefersAudioDescriptions: accessData.prefers_audio_descriptions ?? false,
            prefersCaptions: accessData.prefers_captions ?? true,
            prefersKeyboardNavigation: accessData.prefers_keyboard_navigation ?? false,
            prefersSimplifiedUI: accessData.prefers_simplified_ui ?? false,
            needsExtraTime: accessData.needs_extra_time ?? false,
            learningStyle: accessData.learning_style ?? [],
            prefersStepByStep: accessData.prefers_step_by_step ?? false,
            prefersVideoTutorials: accessData.prefers_video_tutorials ?? false,
            preferredLanguage: accessData.preferred_language ?? "en",
            readingLevel: (accessData.reading_level as AccessibilityPreferences["readingLevel"]) ?? "standard",
          });
        }

        // Load cultural preferences
        const { data: culturalData } = await supabase
          .from("user_cultural_preferences")
          .select("preferred_pronouns, custom_pronouns, preferred_honorific, custom_honorific, cultural_background, primary_language_at_home, prefers_culturally_diverse_examples, regional_content_preference, content_sensitivity_flags")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (culturalData) {
          setCulturalPreferences({
            preferredPronouns: culturalData.preferred_pronouns as CulturalPreferences["preferredPronouns"],
            customPronouns: culturalData.custom_pronouns,
            preferredHonorific: culturalData.preferred_honorific as CulturalPreferences["preferredHonorific"],
            customHonorific: culturalData.custom_honorific,
            culturalBackground: culturalData.cultural_background ?? [],
            primaryLanguageAtHome: culturalData.primary_language_at_home,
            prefersCulturallyDiverseExamples: culturalData.prefers_culturally_diverse_examples ?? true,
            regionalContentPreference: culturalData.regional_content_preference,
            contentSensitivityFlags: culturalData.content_sensitivity_flags ?? [],
          });
        }
      } catch (error) {
        console.error("Error loading accessibility preferences:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  // Persist to localStorage whenever preferences change
  useEffect(() => {
    localStorage.setItem("accessibility_preferences", JSON.stringify(preferences));
  }, [preferences]);

  const updatePreferences = (updates: Partial<AccessibilityPreferences>) => {
    setPreferences((prev) => ({ ...prev, ...updates }));
  };

  const updateCulturalPreferences = (updates: Partial<CulturalPreferences>) => {
    setCulturalPreferences((prev) => ({ ...prev, ...updates }));
  };

  const savePreferences = async () => {
    if (!user) return;

    try {
      // Upsert accessibility preferences
      await supabase.from("user_accessibility_preferences").upsert({
        user_id: user.id,
        prefers_high_contrast: preferences.prefersHighContrast,
        prefers_large_text: preferences.prefersLargeText,
        prefers_reduced_motion: preferences.prefersReducedMotion,
        color_vision_mode: preferences.colorVisionMode,
        prefers_audio_descriptions: preferences.prefersAudioDescriptions,
        prefers_captions: preferences.prefersCaptions,
        prefers_keyboard_navigation: preferences.prefersKeyboardNavigation,
        prefers_simplified_ui: preferences.prefersSimplifiedUI,
        needs_extra_time: preferences.needsExtraTime,
        learning_style: preferences.learningStyle,
        prefers_step_by_step: preferences.prefersStepByStep,
        prefers_video_tutorials: preferences.prefersVideoTutorials,
        preferred_language: preferences.preferredLanguage,
        reading_level: preferences.readingLevel,
      });

      // Upsert cultural preferences
      await supabase.from("user_cultural_preferences").upsert({
        user_id: user.id,
        preferred_pronouns: culturalPreferences.preferredPronouns,
        custom_pronouns: culturalPreferences.customPronouns,
        preferred_honorific: culturalPreferences.preferredHonorific,
        custom_honorific: culturalPreferences.customHonorific,
        cultural_background: culturalPreferences.culturalBackground,
        primary_language_at_home: culturalPreferences.primaryLanguageAtHome,
        prefers_culturally_diverse_examples: culturalPreferences.prefersCulturallyDiverseExamples,
        regional_content_preference: culturalPreferences.regionalContentPreference,
        content_sensitivity_flags: culturalPreferences.contentSensitivityFlags,
      });
    } catch (error) {
      console.error("Error saving preferences:", error);
      throw error;
    }
  };

  const applyAccessibilityClasses = (): string => {
    const classes: string[] = [];
    
    if (preferences.prefersHighContrast) classes.push("high-contrast");
    if (preferences.prefersLargeText) classes.push("large-text");
    if (preferences.prefersReducedMotion) classes.push("reduced-motion");
    if (preferences.prefersSimplifiedUI) classes.push("simplified-ui");
    if (preferences.colorVisionMode) classes.push(`color-vision-${preferences.colorVisionMode}`);
    
    return classes.join(" ");
  };

  return (
    <AccessibilityContext.Provider
      value={{
        preferences,
        culturalPreferences,
        updatePreferences,
        updateCulturalPreferences,
        savePreferences,
        isLoading,
        applyAccessibilityClasses,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
